// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getFirestore, initializeFirestore } from "firebase/firestore";
import {
  initializeAuth,
  getAuth,
  GoogleAuthProvider,
  browserLocalPersistence,
  indexedDBLocalPersistence,
  browserPopupRedirectResolver,
} from "firebase/auth";

/**
 * OAuth redirect state is tied to authDomain + origin. If the app runs on hr.acadxl.com but
 * authDomain stays *.firebaseapp.com, mobile browsers (storage-partitioned) often lose
 * sessionStorage and show "missing initial state" after Google redirects back.
 * Set REACT_APP_FIREBASE_AUTH_DOMAIN=hr.acadxl.com in production, or rely on the runtime
 * hostname match below. The domain must be added in Firebase Console → Auth → Authorized
 * domains. On Vercel, vercel.json rewrites /__/auth/* to *.firebaseapp.com so the handler
 * loads (without this, OAuth can show a blank page on your custom domain).
 *
 * redirect_uri_mismatch (phone): Google Cloud → APIs & Services → Credentials → OAuth 2.0
 * Client ID (Web, auto-created for Firebase) → Authorized redirect URIs must include exactly:
 *   https://<authDomain>/__/auth/handler
 * e.g. https://hr.acadxl.com/__/auth/handler — Firebase default *.firebaseapp.com URIs alone are not enough when authDomain is your custom host.
 */
function resolveAuthDomain() {
  // If custom-domain auth breaks (handler not on your host), set in build env:
  // REACT_APP_FIREBASE_USE_DEFAULT_AUTH_DOMAIN=true
  if (process.env.REACT_APP_FIREBASE_USE_DEFAULT_AUTH_DOMAIN === 'true') {
    return 'hr-dashboard-a9895.firebaseapp.com';
  }
  const fromEnv = process.env.REACT_APP_FIREBASE_AUTH_DOMAIN;
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1') {
      return 'hr-dashboard-a9895.firebaseapp.com';
    }
    // Custom prod — always canonical apex so OAuth needs one redirect URI (www vs non-www otherwise = redirect_uri_mismatch on one device).
    // Keep both hosts in Firebase → Auth → Authorized domains.
    if (h === 'hr.acadxl.com' || h === 'www.hr.acadxl.com') {
      return 'hr.acadxl.com';
    }
    // Vercel preview/production: if authDomain stays *.firebaseapp.com but the app URL is *.vercel.app,
    // mobile Safari often loses redirect OAuth state ("missing initial state"). Match the real host.
    if (h.endsWith('.vercel.app')) {
      return h;
    }
  }
  return 'hr-dashboard-a9895.firebaseapp.com';
}

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'AIzaSyCRVCFsg6QquqWRjxV-7n5Ri44TXwnWyq4',
  authDomain: resolveAuthDomain(),
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'hr-dashboard-a9895',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'hr-dashboard-a9895.firebasestorage.app',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '975081595029',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '1:975081595029:web:2f156cbe6e589c81c6bb9c',
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || 'G-R4QJLBRW2T'
};

let app;
let analytics;
let db;
let auth;
const googleProvider = new GoogleAuthProvider();
// Avoid prompt=select_account here: some mobile WebViews return auth/argument-error on redirect.
googleProvider.addScope('email');
googleProvider.addScope('profile');

// initializeAuth + popupRedirectResolver: required for reliable signInWithRedirect on mobile
// (plain getAuth was a regression for some devices after recent updates).
try {
  app = initializeApp(firebaseConfig);
  try {
    auth = initializeAuth(app, {
      persistence: [indexedDBLocalPersistence, browserLocalPersistence],
      popupRedirectResolver: browserPopupRedirectResolver,
    });
  } catch (e) {
    if (e?.code === 'auth/already-initialized') {
      auth = getAuth(app);
    } else {
      throw e;
    }
  }
  try {
    // Long polling avoids WebChannel hangs on some networks / VPNs / proxies (common Firestore timeout in console).
    db = initializeFirestore(app, {
      experimentalForceLongPolling: true,
    });
  } catch (e) {
    // Hot reload / duplicate init: fall back to default instance
    db = getFirestore(app);
  }
  // IndexedDB persistence disabled: avoids Firestore SDK "INTERNAL ASSERTION FAILED"
  // (TargetState / WatchChangeAggregator) with collectionGroup listeners + React Strict Mode.
  try {
    analytics = getAnalytics(app);
  } catch (aErr) {
    console.warn('Firebase Analytics unavailable (optional):', aErr?.message || aErr);
  }
} catch (error) {
  console.error('Firebase initialization error:', error);
}

export { app, db, analytics, auth, googleProvider };
